<template>
  <Teleport :to="portalRootElementSelector">
    <Transition
      :enter-active-class="
        largeDesktop
          ? 'animate__animated animate__slideInUp'
          : 'animate__animated animate__slideInDown'
      "
      :leave-active-class="
        largeDesktop
          ? 'animate__animated animate__slideOutDown'
          : 'animate__animated animate__slideOutUp'
      "
      :style="{ '--animate-duration': '250ms' }"
    >
      <div
        v-if="!isPending && appStore.cookieConsent === null"
        class="fixed inset-x-0 w-full flex justify-center items-center z-[999999999] max-large-desktop:top-0 large-desktop:bottom-0"
      >
        <div
          class="flex flex-wrap items-center bg-fill-brand-secondary backdrop-blur-[100px] text-text w-full p-3 gap-2.5 large-desktop:flex-row large-desktop:max-w-[800px] large-desktop:px-4 large-desktop:py-2.5 large-desktop:rounded-lg"
        >
          <div class="flex flex-1 items-center">
            <p
              class="text-sm font-medium leading-5 min-w-32 xs:whitespace-nowrap"
            >
              {{ $t("cookieConsentBanner.message") }}
            </p>
          </div>

          <div class="flex flex-wrap justify-center items-center gap-3">
            <ButtonV2
              variant="subtle"
              size="sm"
              class="flex-1"
              @click="appStore.showCookieSettings = true"
            >
              <span class="text-sm font-medium leading-5 whitespace-nowrap">{{
                $t("cookieConsentBanner.settings")
              }}</span>
            </ButtonV2>
            <ButtonV2
              variant="primary"
              size="sm"
              class="flex-1"
              @click="denyAllCookies"
            >
              <span class="text-sm font-medium leading-5 whitespace-nowrap">{{
                $t("cookieConsentBanner.rejectAll")
              }}</span>
            </ButtonV2>
            <ButtonV2
              variant="primary"
              size="sm"
              class="flex-1"
              @click="acceptAllCookies"
            >
              <span class="text-sm font-medium leading-5 whitespace-nowrap">{{
                $t("cookieConsentBanner.acceptAll")
              }}</span>
            </ButtonV2>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
<script setup lang="ts">
import { useAppStore } from "@/stores/app.store";
import ButtonV2 from "../ui/button/button-v2.vue";
import {
  createAcceptAllConsent,
  createDenyAllConsent,
  type CookieConsent,
} from "@/utils/cookies";
import { portalRootElementSelector } from "@/constants/ui";
import { useResponsiveHelpers } from "@/composables/global/use-responsive-helpers";
import { useTimeoutFn } from "@vueuse/core";
import { useMutation } from "@urql/vue";
import { graphql as gql } from "@/generated";

const appStore = useAppStore();
const { largeDesktop } = useResponsiveHelpers();
const { isPending } = useTimeoutFn(() => {}, 3000, { immediate: true });

const LogCookieConsentDocument = gql(/* GraphQL */ `
  mutation LogCookieConsent(
    $marketing: Boolean!
    $personalization: Boolean!
    $analytics: Boolean!
  ) {
    logCookieConsent(
      marketing: $marketing
      personalization: $personalization
      analytics: $analytics
    )
  }
`);

const { executeMutation: executeLogCookieConsent } = useMutation(
  LogCookieConsentDocument,
);

function logCookieConsent(consent: CookieConsent) {
  executeLogCookieConsent({
    marketing: consent.marketing,
    personalization: consent.personalization,
    analytics: consent.analytics,
  });
}

function denyAllCookies() {
  const consent = createDenyAllConsent();
  appStore.cookieConsent = consent;
  logCookieConsent(consent);
}

function acceptAllCookies() {
  const consent = createAcceptAllConsent();
  appStore.cookieConsent = consent;
  logCookieConsent(consent);
}
</script>
